import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Input, Select } from '../atoms';
import { Modal } from '../organisms';
import { AddOptionInline } from '../organisms/InlineEditCard';
import { useOrgTimezone, zonedInputToUtcIso, utcIsoToZonedInput, formatAbsInTz, formatDate } from '../lib/orgTimezone';
import { humanizeToken } from '../lib/inlineAttribution';

/**
 * CalendarShared — the pieces the org Calendar (CalendarPage) and the per-client
 * Calendar tab (ClientDetail) both render, so the two surfaces stay identical:
 *   • the CalendarEvent shape (incl. the joined linkage NAMES the backend now
 *     returns — client_name / parcel_name / parcel_account_number / property_name);
 *   • the event-type metadata (label + colour);
 *   • the create/edit EventModal (with an optional locked client for the
 *     per-client tab, and property_id derived from the chosen parcel);
 *   • the EventLinkage renderer that shows the client → property → parcel chain
 *     on every event row so events read as connected, not "unlinked".
 */

export type EventType = 'appeal_deadline' | 'hearing' | 'tax_bill' | 'reminder' | 'rendition_deadline';

/**
 * B4 — is this date a placeholder or firm? Every pre-existing event reads
 * 'confirmed' (the column default), so nothing silently becomes tentative.
 */
export type DateConfidence = 'tentative' | 'confirmed';

export interface CalendarEvent {
  id: string;
  organization_id: string;
  parcel_id: string | null;
  client_id: string | null;
  property_id: string | null;
  /** One of the five built-ins, or a type the firm added to its calendar-event-types list (205). */
  event_type: EventType | string;
  title: string | null;
  event_date: string; // YYYY-MM-DD
  event_time: string | null;
  location: string | null;
  state: string | null;
  assigned_user_id: string | null;
  assigned_contact_id: string | null;
  status: string | null;
  remind_at: string | null;
  source: string | null;
  notes: string | null;
  /** B4 — 'tentative' | 'confirmed'. Optional on the wire so an older cached row still parses. */
  date_confidence?: DateConfidence | null;
  /** 205 (ATA part 3 §9): who confirmed a government date and when, and the government site it came from. */
  verified_by_user_id?: string | null;
  verified_at?: string | null;
  source_url?: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  // Joined linkage names (read-only, from the LEFT JOINs in the service). NULL
  // when the event has no such link.
  client_name?: string | null;
  parcel_name?: string | null;
  parcel_account_number?: string | null;
  property_name?: string | null;
}

export interface UserLite {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}
export interface NamedRow { id: string; name: string }
/** A parcel option carries its property_id/account so the modal can derive the property link. */
export interface ParcelOption { id: string; name: string | null; property_id?: string | null; account_number?: string | null }
/** A property option carries its client_id so choosing a property can auto-fill the client link. */
export interface PropertyOption { id: string; name: string | null; client_id?: string | null }

export const TYPE_META: Record<EventType, { label: string; variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger'; accent: string }> = {
  appeal_deadline: { label: 'Appeal deadline', variant: 'danger', accent: 'var(--color-status-red)' },
  hearing: { label: 'Hearing', variant: 'warning', accent: 'var(--color-status-amber)' },
  tax_bill: { label: 'Tax bill', variant: 'info', accent: 'var(--color-status-blue)' },
  reminder: { label: 'Reminder', variant: 'neutral', accent: 'var(--color-muted)' },
  rendition_deadline: { label: 'BPP rendition', variant: 'danger', accent: 'var(--color-status-red)' },
};
export const TYPE_ORDER: EventType[] = ['appeal_deadline', 'hearing', 'rendition_deadline', 'tax_bill', 'reminder'];

/**
 * Is this event deadline-shaped — a date you can MISS (any `*_deadline` type)
 * or must attend (a hearing)?
 *
 * Shape-based, not a name list, deliberately: the "Deadlines only" filter used
 * to hardcode `appeal_deadline` + `hearing`, so when `rendition_deadline`
 * arrived the filter silently HID rendition deadlines — an operator looking at
 * ten of them checked the box and watched their deadlines vanish (it was a top
 * rage-click target in session replay before this fix). Matching the `_deadline`
 * suffix means the next deadline type joins the filter the day it is born
 * instead of re-breaking it.
 */
export function isDeadlineEvent(t: string | null | undefined): boolean {
  return !!t && (t.endsWith('_deadline') || t === 'hearing');
}

/**
 * Safe lookup for a DB-sourced event_type. TYPE_META is typed as total over EventType,
 * but the stored value could be a type added via a dropped CHECK without updating the
 * map (this codebase drops enum CHECKs to make them org-configurable — that is exactly
 * how the re_pp crash arose). An unknown key falls back instead of `undefined.label`
 * white-screening the Calendar, the Dashboard upcoming panel, and the client calendar.
 */
type EventMeta = { label: string; variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger'; accent: string };
const DEFAULT_EVENT_META: EventMeta = { label: 'Event', variant: 'neutral', accent: 'var(--color-ink-3)' };
export function metaFor(t: string | null | undefined): EventMeta {
  return (TYPE_META as Record<string, EventMeta>)[t ?? ''] ?? DEFAULT_EVENT_META;
}

// ── B4: tentative vs confirmed ───────────────────────────────────────────────

/**
 * Is this event's date still a placeholder? Anything but an explicit 'tentative'
 * reads as firm — including a row from before the column existed — so the
 * distinction can only ever make a date LESS authoritative than today, never more.
 */
export function isTentative(e: { date_confidence?: DateConfidence | null }): boolean {
  return e.date_confidence === 'tentative';
}

/**
 * The one visual marker for a placeholder date, used everywhere an event renders
 * (month cell, agenda row, upcoming rail, the event profile) so "tentative" reads
 * the same on every surface. The whole point of B4 is that a consultant planning
 * around a date can see at a glance that it may still move.
 */
export function TentativeBadge({ compact }: { compact?: boolean }) {
  if (compact) {
    return (
      <span
        title="Tentative — this date is not confirmed yet"
        style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-status-amber, var(--color-muted))',
        }}
      >TENT</span>
    );
  }
  return <Badge variant="warning">Tentative</Badge>;
}

/**
 * The card/row treatment for a tentative event: a DASHED outline instead of the
 * solid one a firm date gets. Dashed is the convention for "provisional" and it
 * survives the badge being clipped in a dense month cell.
 */
export function tentativeRowStyle(e: { date_confidence?: DateConfidence | null }): CSSProperties {
  return isTentative(e) ? { borderStyle: 'dashed', borderWidth: 1 } : {};
}

// ── ATA part 3 §9: government dates are seed data until an admin confirms them ──

/** A government-sourced event: the sync projected it from an assessor, collector or bill date. */
export function isGovernmentDate(e: { id: string; source?: string | null }): boolean {
  return e.id.startsWith('sync-') && !!e.source;
}

/** The verification standing of a government date, or null for a hand-authored event. */
export function governmentStanding(e: { id: string; source?: string | null; date_confidence?: DateConfidence | null }): 'unverified' | 'confirmed' | null {
  if (!isGovernmentDate(e)) return null;
  return isTentative(e) ? 'unverified' : 'confirmed';
}

/** Unverified (amber) or Confirmed (green) on a government date; nothing on a hand-authored event. */
export function GovernmentStandingBadge({ event }: { event: { id: string; source?: string | null; date_confidence?: DateConfidence | null } }) {
  const standing = governmentStanding(event);
  if (!standing) return null;
  return standing === 'confirmed'
    ? <Badge variant="success" title="Confirmed with the jurisdiction">Confirmed</Badge>
    : <Badge variant="warning" title="Seed date, not yet confirmed with the jurisdiction">Unverified</Badge>;
}

/** The label a type shows: the built-in label, or the firm's own type name as typed. */
export function eventTypeLabel(t: string | null | undefined): string {
  if (!t) return 'Event';
  return (TYPE_META as Record<string, { label: string }>)[t]?.label ?? t;
}

/** Today as YYYY-MM-DD (local), the default date for a fresh event. */
export function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function userLabel(users: UserLite[], uid: string | null): string {
  if (!uid) return '';
  const u = users.find((x) => x.id === uid);
  if (!u) return uid;
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
}

/**
 * eventTitle — the human-facing title for an event row.
 *
 * Auto-generated titles append the raw parcel id slug after a dash
 * ("Protest deadline — demo-parcel-bluebonnet-1-1"). The parcel already renders
 * as its own linked chip (see EventLinkage), so that trailing machine slug is
 * redundant noise — strip it. We only remove a suffix that exactly matches the
 * event's own parcel_id (or any residual `…-…` id-shaped slug with no spaces),
 * so a legitimately prose title like "Call re: comps — bring packet" is
 * untouched. Falls back to the type label when the title is empty.
 */
/**
 * sourceLabel — a short human label for an event's `source` (calendar cluster).
 * Government-synced events carry a machine source tag (from services/
 * calendar-sync.js); render it as a readable origin. Hand-authored events
 * (source 'demo' / 'workflow' / null) get no chip.
 */
export function sourceLabel(source: string | null | undefined): string | null {
  switch (source) {
    case 'collector': return 'Collector';
    case 'collector_delinquent': return 'Collector · delinquent';
    case 'assessor': return 'Assessor';
    case 'bpp_rendition': return 'BPP rendition';
    default: return null;
  }
}

export function eventTitle(e: CalendarEvent): string {
  const fallback = metaFor(e.event_type).label;
  let title = e.title?.trim();
  if (!title) return fallback;
  const stripSuffix = (slug: string) => {
    // A dash separator (—, –, or -) padded by spaces, then the slug, at the end.
    const i = title!.search(/\s+[—–-]\s+/);
    if (i === -1) return;
    const sep = title!.slice(i).match(/\s+[—–-]\s+/)![0];
    const tail = title!.slice(i + sep.length);
    if (tail === slug) title = title!.slice(0, i).trim();
  };
  if (e.parcel_id) stripSuffix(e.parcel_id);
  // Safety net: an unambiguous seed id slug tail (`— demo-…`) if parcel_id was absent.
  title = title.replace(/\s+[—–-]\s+demo-[\w-]+$/i, '').trim();
  return title || fallback;
}

/**
 * A source-record link an event resolves to. The event carries FK links to the
 * client → property → parcel chain (the joined names the backend returns); each
 * becomes a real route into that record's detail page. Kept in one place so the
 * grid cell, the agenda row, and the modal all click through identically.
 */
export interface SourceLink { key: 'parcel' | 'property' | 'client'; to: string; label: string }

/**
 * Every navigable source record an event links to, most-specific first (parcel →
 * property → client). Uses ONLY the FK ids the event already carries — no new
 * projection needed — routed to the app's existing detail pages
 * (/parcels/:id, /properties/:id, /clients/:id).
 */
export function eventSourceLinks(event: CalendarEvent): SourceLink[] {
  const links: SourceLink[] = [];
  if (event.parcel_id) {
    const label = event.parcel_name
      ? `${event.parcel_name}${event.parcel_account_number ? ` · #${event.parcel_account_number}` : ''}`
      : 'Parcel';
    links.push({ key: 'parcel', to: `/parcels/${event.parcel_id}`, label });
  }
  if (event.property_id) {
    links.push({ key: 'property', to: `/properties/${event.property_id}`, label: event.property_name || 'Property' });
  }
  if (event.client_id) {
    links.push({ key: 'client', to: `/clients/${event.client_id}`, label: event.client_name || 'Client' });
  }
  return links;
}

/** The single best source record for an event (most-specific), or null. */
export function primarySourceLink(event: CalendarEvent): SourceLink | null {
  return eventSourceLinks(event)[0] ?? null;
}

/**
 * EventLinkage — the connected-record line shown under an event: the client, the
 * parcel (with account #), and the property it hangs off, drawn from the joined
 * names the backend returns. Each chip is a real Link that CLICKS THROUGH to that
 * source record's detail page. Renders nothing when the event has no links.
 */
export function EventLinkage({ event, style }: { event: CalendarEvent; style?: React.CSSProperties }) {
  const parcelLabel = event.parcel_name
    ? `${event.parcel_name}${event.parcel_account_number ? ` · #${event.parcel_account_number}` : ''}`
    : null;
  const chips: Array<{ key: string; to: string | null; label: string; variant: 'info' | 'neutral' | 'success' }> = [];
  if (event.client_name) chips.push({ key: 'client', to: event.client_id ? `/clients/${event.client_id}` : null, label: event.client_name, variant: 'info' });
  if (parcelLabel) chips.push({ key: 'parcel', to: event.parcel_id ? `/parcels/${event.parcel_id}` : null, label: parcelLabel, variant: 'neutral' });
  else if (event.property_name) chips.push({ key: 'property', to: event.property_id ? `/properties/${event.property_id}` : null, label: event.property_name, variant: 'neutral' });
  if (chips.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', ...style }}>
      {chips.map((c) => (
        c.to ? (
          <Link
            key={c.key}
            to={c.to}
            onClick={(e) => e.stopPropagation()}
            title={`Open ${c.key}: ${c.label}`}
            style={{ textDecoration: 'none' }}
          >
            <Badge variant={c.variant}>{c.label} ↗</Badge>
          </Link>
        ) : (
          <Badge key={c.key} variant={c.variant}>{c.label}</Badge>
        )
      ))}
    </div>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────
export function EventModal({
  event, defaultDate, users, parcels, clients, properties = [], lockClient, canEdit, canDelete, saving, deleting, onClose, onSubmit, onDelete,
  onSetDateConfidence, settingConfidence, allowedTypes,
}: {
  event: CalendarEvent | null;
  defaultDate: string;
  users: UserLite[];
  parcels: ParcelOption[];
  clients: NamedRow[];
  /** Property options (optional — the picker degrades to "No property" when empty). */
  properties?: PropertyOption[];
  /** When set, the client is fixed (per-client tab): picker becomes read-only + defaults the link. */
  lockClient?: { id: string; name: string };
  canEdit: boolean;
  canDelete: boolean;
  saving: boolean;
  deleting: boolean;
  onClose: () => void;
  onSubmit: (body: Partial<CalendarEvent>) => void;
  onDelete?: () => void;
  /**
   * B4 — move an existing event's date between tentative and confirmed. A governed
   * transition on its own endpoint, not part of the save body, so it is a separate
   * callback. Omitted (per-client tab) → the control is not offered.
   */
  onSetDateConfidence?: (to: DateConfidence, eventDate?: string) => void;
  settingConfidence?: boolean;
  /** 205: the firm's event types (built-ins + its own). Defaults to the built-ins. */
  allowedTypes?: string[];
}) {
  // The org timezone governs how remind_at (an absolute UTC instant) is edited:
  // the datetime-local field shows/accepts the ORG-local wall clock, so an 8:00
  // reminder is stored as the correct UTC instant for 8:00 org-local — never the
  // setter's browser zone (calendar-tz-grid fix #4).
  const orgTz = useOrgTimezone();
  const [eventType, setEventType] = useState<string>(event?.event_type ?? 'reminder');
  const typeOptions = Array.from(new Set([...TYPE_ORDER, ...(allowedTypes ?? []), ...(event?.event_type ? [event.event_type] : [])]))
    .map((t) => ({ value: t, label: eventTypeLabel(t) }));
  // §9: an admin confirms a government date after calling the jurisdiction, and may move it to the date they gave.
  const government = !!event && isGovernmentDate(event);
  const [verifyDate, setVerifyDate] = useState(event?.event_date ?? '');
  const [title, setTitle] = useState(event?.title ?? '');
  const [eventDate, setEventDate] = useState(event?.event_date ?? defaultDate);
  const [eventTime, setEventTime] = useState(event?.event_time ?? '');
  const [location, setLocation] = useState(event?.location ?? '');
  const [assignedUserId, setAssignedUserId] = useState(event?.assigned_user_id ?? '');
  const [parcelId, setParcelId] = useState(event?.parcel_id ?? '');
  const [clientId, setClientId] = useState(event?.client_id ?? lockClient?.id ?? '');
  const [propertyId, setPropertyId] = useState(event?.property_id ?? '');
  const [remindAt, setRemindAt] = useState(event?.remind_at ? utcIsoToZonedInput(event.remind_at, orgTz) : '');
  const [status, setStatus] = useState(event?.status ?? '');
  const [notes, setNotes] = useState(event?.notes ?? '');
  // B4 — settable only while CREATING. On an existing event the standing moves
  // through its own transition endpoint (see onSetDateConfidence), so the backend
  // drops it from a generic save and offering it in this form would be a lie.
  const [dateConfidence, setDateConfidence] = useState<DateConfidence>(event?.date_confidence ?? 'confirmed');

  const isEdit = !!event;
  const disabled = !canEdit || saving;

  // Opening an existing event lands on a read-only PROFILE first (the reviewer's
  // ask — clicking an event must not jump straight into an editable form); the
  // Edit button flips to the form below. Creating a new event skips straight to
  // the form. The modal remounts per event (keyed in CalendarPage), so this
  // initializes fresh on every open.
  const [mode, setMode] = useState<'view' | 'edit'>(event ? 'view' : 'edit');

  // The three linkage pickers (Client → Property → Parcel) cascade so the
  // client → property → parcel chain stays consistent no matter which the user
  // picks first. Every derivation is fill-if-empty / correct-if-inconsistent —
  // it never wipes a still-consistent explicit choice.
  const effectiveClientId = lockClient?.id ?? clientId;

  // Property options are scoped to the chosen client (properties whose client_id
  // matches, plus any with an unknown client_id so nothing is hidden by accident).
  const propertyOptions = useMemo(() => (
    effectiveClientId
      ? properties.filter((p) => p.client_id == null || p.client_id === effectiveClientId)
      : properties
  ), [properties, effectiveClientId]);

  // Parcel options are scoped to the chosen property (a property with no parcel
  // still saves — the parcel picker just shows "No parcel").
  const parcelOptions = useMemo(() => (
    propertyId ? parcels.filter((p) => p.property_id === propertyId) : parcels
  ), [parcels, propertyId]);

  // Selecting a parcel derives BOTH its property and (via the property) its
  // client, so the full chain is filled from the most-specific pick.
  const onParcelChange = (nextParcelId: string) => {
    setParcelId(nextParcelId);
    const p = parcels.find((x) => x.id === nextParcelId);
    if (p && p.property_id) {
      setPropertyId(p.property_id);
      const prop = properties.find((x) => x.id === p.property_id);
      if (prop?.client_id && !lockClient) setClientId(prop.client_id);
    } else if (!nextParcelId) {
      setPropertyId(event?.property_id ?? '');
    }
  };

  // Selecting a property fills the client (when not locked) and drops a parcel
  // that no longer belongs to the chosen property.
  const onPropertyChange = (nextPropertyId: string) => {
    setPropertyId(nextPropertyId);
    const prop = properties.find((x) => x.id === nextPropertyId);
    if (nextPropertyId && prop?.client_id && !lockClient) setClientId(prop.client_id);
    if (nextPropertyId) {
      const cur = parcels.find((x) => x.id === parcelId);
      if (cur && cur.property_id !== nextPropertyId) setParcelId('');
    }
  };

  // Changing the client clears a property/parcel that belongs to a DIFFERENT
  // client (only when known-inconsistent — never a still-valid choice).
  const onClientChange = (nextClientId: string) => {
    setClientId(nextClientId);
    if (nextClientId && propertyId) {
      const prop = properties.find((x) => x.id === propertyId);
      if (prop?.client_id && prop.client_id !== nextClientId) {
        setPropertyId('');
        setParcelId('');
      }
    }
  };

  const submit = () => {
    if (!eventDate) return;
    const body: Partial<CalendarEvent> = {
      event_type: eventType,
      title: title || null,
      event_date: eventDate,
      event_time: eventTime || null,
      location: location || null,
      assigned_user_id: assignedUserId || null,
      parcel_id: parcelId || null,
      client_id: (lockClient?.id ?? clientId) || null,
      property_id: propertyId || null,
      remind_at: remindAt ? zonedInputToUtcIso(remindAt, orgTz) : null,
      status: status || null,
      notes: notes || null,
    };
    // Only on create — the backend ignores it on an edit by design.
    if (!isEdit) body.date_confidence = dateConfidence;
    onSubmit(body);
  };

  // ── Read-only event profile (view mode) ─────────────────────────────────────
  if (mode === 'view' && event) {
    const links = eventSourceLinks(event);
    const assignee = userLabel(users, event.assigned_user_id);
    return (
      <Modal
        open
        onClose={onClose}
        title="Event details"
        size="lg"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 'var(--sp-2)' }}>
            <div>
              {canDelete && onDelete && (
                <Button variant="ghost" onClick={onDelete} disabled={deleting} style={{ color: 'var(--color-status-red)' }}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              {/* B4 — the confirm action, right where someone reads the date. §9: on a government
                  date the admin may also type the date the jurisdiction gave; it writes through
                  to the assessor, collector or bill the date came from. */}
              {canEdit && onSetDateConfidence && government && isTentative(event) && (
                <Input
                  type="date"
                  aria-label="Date the jurisdiction gave"
                  value={verifyDate}
                  onChange={(e) => setVerifyDate(e.target.value)}
                  disabled={settingConfidence}
                />
              )}
              {canEdit && onSetDateConfidence && (
                <Button
                  variant={isTentative(event) ? 'primary' : 'ghost'}
                  disabled={settingConfidence}
                  onClick={() => onSetDateConfidence(
                    isTentative(event) ? 'confirmed' : 'tentative',
                    government && isTentative(event) && verifyDate && verifyDate !== event.event_date ? verifyDate : undefined,
                  )}
                >
                  {settingConfidence
                    ? 'Saving…'
                    : isTentative(event) ? (government ? 'Mark confirmed' : 'Confirm date') : (government ? 'Mark unverified' : 'Mark tentative')}
                </Button>
              )}
              <Button variant="secondary" onClick={onClose}>Close</Button>
              {canEdit && <Button variant="primary" onClick={() => setMode('edit')}>Edit</Button>}
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Badge variant={metaFor(event.event_type).variant}>{eventTypeLabel(event.event_type)}</Badge>
            <GovernmentStandingBadge event={event} />
            {event.source_url && (
              <a href={event.source_url} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--fs-sm)', color: 'var(--brand-primary)' }}>Government site ↗</a>
            )}
            {isTentative(event) && <TentativeBadge />}
            <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{eventTitle(event)}</span>
          </div>
          {links.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)',
              padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-surface-2, rgba(0,0,0,0.03))',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
            }}>
              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Linked to</span>
              {links.map((l) => (
                <Link key={l.key} to={l.to} onClick={onClose} style={{ textDecoration: 'none' }}>
                  <Badge variant={l.key === 'client' ? 'info' : 'neutral'}>{l.label} ↗</Badge>
                </Link>
              ))}
            </div>
          )}
          <ViewRow label="When">
            {formatDate(event.event_date)}{event.event_time ? ` · ${event.event_time}` : ''}
            {isTentative(event) && (
              <span style={{ marginLeft: 'var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                — tentative, not confirmed yet
              </span>
            )}
          </ViewRow>
          {event.location && <ViewRow label="Location">{event.location}</ViewRow>}
          <ViewRow label="Assigned to">{assignee || 'Unassigned'}</ViewRow>
          {event.remind_at && <ViewRow label="Reminder">{`${formatAbsInTz(event.remind_at, orgTz)} · ${orgTz}`}</ViewRow>}
          {event.status && <ViewRow label="Status">{humanizeToken(event.status)}</ViewRow>}
          {event.notes && <ViewRow label="Notes"><span style={{ whiteSpace: 'pre-wrap' }}>{event.notes}</span></ViewRow>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit event' : 'New event'}
      size="lg"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 'var(--sp-2)' }}>
          <div>
            {isEdit && canDelete && onDelete && (
              <Button variant="ghost" onClick={onDelete} disabled={deleting} style={{ color: 'var(--color-status-red)' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button variant="secondary" onClick={() => (isEdit ? setMode('view') : onClose())}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={disabled || !eventDate}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {isEdit && event && eventSourceLinks(event).length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)',
            padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-surface-2, rgba(0,0,0,0.03))',
            border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
          }}>
            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>
              Linked to
            </span>
            {eventSourceLinks(event).map((l) => (
              <Link key={l.key} to={l.to} onClick={onClose} style={{ textDecoration: 'none' }}>
                <Badge variant={l.key === 'client' ? 'info' : 'neutral'}>{l.label} ↗</Badge>
              </Link>
            ))}
          </div>
        )}
        <Field label="Type">
          <Select
            aria-label="Type"
            options={typeOptions}
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            disabled={disabled}
          />
          {/* §9: the firm's list grows from here; the new type lands in Org Settings too. */}
          {!disabled && <AddOptionInline slug="calendar-event-types" onAdded={(v) => setEventType(v)} />}
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" disabled={disabled} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field label="Date">
            <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Time">
            <Input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} disabled={disabled} />
          </Field>
        </div>
        {/* B4 — a hearing date is often tentative when it first arrives. Offered on
            CREATE only; afterwards the standing moves through its own audited
            transition (the Confirm date button on the event profile). */}
        {!isEdit ? (
          <Field label="Date standing">
            <Select
              aria-label="Date standing"
              options={[
                { value: 'confirmed', label: 'Confirmed — this date is firm' },
                { value: 'tentative', label: 'Tentative — not confirmed yet' },
              ]}
              value={dateConfidence}
              onChange={(e) => setDateConfidence(e.target.value as DateConfidence)}
              disabled={disabled}
            />
          </Field>
        ) : event && isTentative(event) ? (
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            This date is <strong>tentative</strong>. Use “Confirm date” on the event profile once it is firm.
          </div>
        ) : null}
        <Field label="Location">
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. ARB Hearing Room 2" disabled={disabled} />
        </Field>
        <Field label="Assign to">
          <Select
            options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: `${[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email} (${u.role})` }))]}
            value={assignedUserId}
            onChange={(e) => setAssignedUserId(e.target.value)}
            disabled={disabled}
          />
        </Field>
        {/* Three linked pickers — Client → Property → Parcel. Choosing any one
            cascades to fill the others so the linkage chain is consistent. */}
        <Field label="Client">
          {lockClient ? (
            <Input value={lockClient.name} disabled readOnly aria-label="Client" />
          ) : (
            <Select
              aria-label="Client"
              options={[{ value: '', label: 'No client' }, ...clients.map((c) => ({ value: c.id, label: c.name || c.id }))]}
              value={clientId}
              onChange={(e) => onClientChange(e.target.value)}
              disabled={disabled}
            />
          )}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field label="Property">
            <Select
              aria-label="Property"
              options={[{ value: '', label: 'No property' }, ...propertyOptions.map((p) => ({ value: p.id, label: p.name || p.id }))]}
              value={propertyId}
              onChange={(e) => onPropertyChange(e.target.value)}
              disabled={disabled}
            />
          </Field>
          <Field label="Parcel">
            <Select
              aria-label="Parcel"
              options={[{ value: '', label: 'No parcel' }, ...parcelOptions.map((p) => ({ value: p.id, label: p.name || p.id }))]}
              value={parcelId}
              onChange={(e) => onParcelChange(e.target.value)}
              disabled={disabled}
            />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field label="Remind at">
            <Input type="datetime-local" value={remindAt} onChange={(e) => setRemindAt(e.target.value)} disabled={disabled} />
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
              {remindAt
                ? `Fires ${formatAbsInTz(zonedInputToUtcIso(remindAt, orgTz), orgTz)} · ${orgTz}`
                : `Org timezone: ${orgTz}`}
            </span>
          </Field>
          <Field label="Status">
            <Input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="e.g. open, done" disabled={disabled} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={disabled}
            rows={3}
            style={{
              fontFamily: 'inherit', fontSize: 'var(--fs-md)', padding: '8px 12px', width: '100%', resize: 'vertical',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)', background: 'var(--color-input-bg, transparent)', color: 'var(--color-ink)',
            }}
          />
        </Field>
      </div>
    </Modal>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{label}</span>
      {children}
    </label>
  );
}

/** A read-only labelled row for the event profile (view mode) — mirrors Field's
 *  label styling but renders a static value instead of an input. */
function ViewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ fontSize: 'var(--fs-md)', color: 'var(--color-ink)' }}>{children}</span>
    </div>
  );
}
